import { isPresent } from '@luminovo/commons';
import React, { useRef, useState } from 'react';

import {
    body,
    bodySemiBold,
    bodySmall,
    bodySmallSemiBold,
    caption,
    code,
    colorSystem,
    h1,
    h2,
    h3,
    h4,
    h5,
} from '../../theme';
import { Tooltip } from '../Tooltip';

export type FontVariant =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'body'
    | 'body-semibold'
    | 'body-small'
    | 'body-small-semibold'
    | 'caption'
    | 'code'
    | 'inherit';

export interface Props extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> {
    variant?: FontVariant;
    color?: string;
    /** Truncates with an ellipsis; `true` also shows the full text in a tooltip, `'ellipsisOnly'` doesn't. */
    showEllipsis?: boolean | 'ellipsisOnly';

    /**
     * When `showEllipsis` is set, allows submitting custom text to be shown in the tooltip.
     * Useful if the default `textContent` behaviour doesnt suit your needs.
     * One example is chips, where the `textContent` returns no whitespace so you see `Resistor6Ω5%50V`.
     */
    ellipsisText?: string;

    /** Tabular numerals, for numbers. */
    tabularNums?: boolean;
}

const variantStyles: Record<FontVariant, React.CSSProperties> = {
    h1: {
        ...h1,
        color: colorSystem.neutral[8],
    },
    h2: {
        ...h2,
        color: colorSystem.neutral[8],
    },
    h3: {
        ...h3,
        color: colorSystem.neutral[8],
    },
    h4: {
        ...h4,
        color: colorSystem.neutral[8],
    },
    h5: {
        ...h5,
        color: colorSystem.neutral[8],
    },
    body: {
        ...body,
        color: colorSystem.neutral[9],
    },
    'body-semibold': {
        ...bodySemiBold,
        color: colorSystem.neutral[9],
    },
    'body-small': {
        ...bodySmall,
        color: colorSystem.neutral[9],
    },
    'body-small-semibold': {
        ...bodySmallSemiBold,
        color: colorSystem.neutral[9],
    },
    caption: {
        ...caption,
        color: colorSystem.neutral[9],
    },
    code: {
        ...code,
        color: colorSystem.blue[6],
    },
    inherit: {},
};

export const ellipsisDefaultStyle: React.CSSProperties = {
    overflow: 'hidden',
    overflowWrap: 'break-word',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    wordBreak: 'break-all',
};

const TextWithEllipsisTooltip = ({ style, showEllipsis, ellipsisText, ...props }: Props) => {
    const [hasOverflowingChildren, setHasOverflowingChildren] = useState(false);
    const [text, setText] = useState<string | null>(null);
    const ref = useRef<HTMLDivElement>(null);

    const handleMouseEnter = React.useCallback(() => {
        if (!isPresent(ref.current) || showEllipsis === 'ellipsisOnly') {
            return;
        }

        const { offsetWidth, scrollWidth, textContent } = ref.current;
        const isOverflowing = offsetWidth < scrollWidth;

        setHasOverflowingChildren(isOverflowing);
        setText((prevText) => (prevText !== textContent ? textContent : prevText));
    }, [setHasOverflowingChildren, setText, showEllipsis]);

    const tooltipTitle = ellipsisText ?? text;

    if (hasOverflowingChildren && isPresent(tooltipTitle)) {
        return (
            <Tooltip title={tooltipTitle} placement="right" sx={{ whiteSpace: 'pre-line' }}>
                <span
                    ref={ref}
                    style={{ ...ellipsisDefaultStyle, ...style }}
                    onMouseEnter={handleMouseEnter}
                    {...props}
                />
            </Tooltip>
        );
    }

    return <span ref={ref} style={{ ...ellipsisDefaultStyle, ...style }} onMouseEnter={handleMouseEnter} {...props} />;
};

export const Text = React.forwardRef<HTMLSpanElement, Props>(function Text(
    {
        variant = 'body',
        color = variantStyles[variant].color,
        showEllipsis,
        ellipsisText,
        tabularNums,
        style,
        ...props
    },
    ref,
) {
    const fontFeatureSettings = variantStyles[variant].fontFeatureSettings || '';

    // Set 'tabular-nums' if enabled
    const fontVariantNumeric = tabularNums ? 'tabular-nums' : undefined;

    const textStyle = { ...variantStyles[variant], fontFeatureSettings, fontVariantNumeric, color, ...style };

    return showEllipsis ? (
        <TextWithEllipsisTooltip
            ref={ref}
            showEllipsis={showEllipsis}
            ellipsisText={ellipsisText}
            style={textStyle}
            {...props}
        />
    ) : (
        <span ref={ref} style={textStyle} {...props} />
    );
});
